/**
 * NEWSLETTER FORM COMPONENT
 * =========================
 * 
 * Loops.so newsletter signup form converted to React.
 * Handles submission, loading states, and error handling.
 */

import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { ArrowLeft } from "lucide-react";
import { readStoredValue, writeStoredValue } from "@/lib/safe-storage";

interface NewsletterFormProps {
  title?: string;
  description?: string;
  className?: string;
}

export function NewsletterForm({ 
  title = "Stay Updated on Decision Science",
  description = "Get the latest insights on decision-making research, frameworks, and practical tips delivered to your inbox weekly.",
  className = ""
}: NewsletterFormProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Oops! Something went wrong, please try again");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    const currentTime = Date.now();
    const lastSubmission = readStoredValue("loops-form-timestamp");
    
    if (lastSubmission && Number(lastSubmission) + 60000 > currentTime) {
      setIsError(true);
      setErrorMessage("Too many signups, please try again in a little while");
      return;
    }
    
    writeStoredValue("loops-form-timestamp", currentTime.toString());
    setIsLoading(true);
    setIsError(false);
    
    try {
      const formBody = `userGroup=Newsletter&mailingLists=&email=${encodeURIComponent(email)}`;
      
      const response = await fetch("https://app.loops.so/api/newsletter-form/cmdqp9ivf0rp30t0ijmxjojst", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody,
      });
      
      if (response.ok) {
        setIsSuccess(true);
        setEmail("");
      } else {
        const data = await response.json().catch(() => ({}));
        setIsError(true);
        setErrorMessage(data.message || response.statusText);
      }
    } catch (error) {
      setIsError(true);
      if (error instanceof Error && error.message === "Failed to fetch") {
        setErrorMessage("Too many signups, please try again in a little while");
      } else {
        setErrorMessage(error instanceof Error ? error.message : "Something went wrong");
      }
      writeStoredValue("loops-form-timestamp", "");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setIsSuccess(false);
    setIsError(false);
    setErrorMessage("Oops! Something went wrong, please try again");
  };

  return (
    <Card className={`max-w-2xl mx-auto bg-primary/5 border-primary/20 ${className}`}>
      <CardContent className="p-8">
        <h3 className="text-2xl font-medium mb-4 text-center">
          {title}
        </h3>
        <p className="text-muted-foreground mb-6 text-center">
          {description}
        </p>
        
        {isSuccess ? (
          <div className="text-center">
            <p className="text-sm font-medium text-green-600 mb-4">
              Thanks! We'll be in touch!
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        ) : isError ? (
          <div className="text-center">
            <p className="text-sm font-medium text-red-600 mb-4">
              {errorMessage}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="flex-1 font-inter"
            />
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-teal-600 hover:bg-teal-700 text-white font-medium font-inter"
            >
              {isLoading ? "Please wait..." : "Join Newsletter"}
            </Button>
          </form>
        )}
        
        {!isSuccess && !isError && (
          <p className="text-sm text-muted-foreground mt-4 text-center">
            No spam, unsubscribe anytime.
          </p>
        )}
      </CardContent>
    </Card>
  );
}